// frontend/src/components/admin/CampaignsList.jsx
import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "../../apiConfig";
import "./midias.css";

// ── Icon (Lucide-style SVG) ────────────────────────────────────────────────
const Icon = ({ name, size = 18, stroke = 1.6, color = "currentColor" }) => {
  const paths = {
    plus:       <><path d="M12 5v14"/><path d="M5 12h14"/></>,
    refresh:    <><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10"/><path d="M20.49 15a9 9 0 01-14.85 3.36L1 14"/></>,
    list:       <><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></>,
    edit:       <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash:      <><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>,
    search:     <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></>,
    x:          <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>,
    check:      <path d="M20 6L9 17l-5-5"/>,
    calendar:   <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    arrowRight: <><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></>,
    arrowDown:  <><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></>,
    filter:     <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>,
    alert:      <><circle cx="12" cy="12" r="9"/><path d="M12 8v4"/><path d="M12 16h.01"/></>,
    play:       <path d="M6 4l14 8-14 8z"/>,
    megaphone:  <><path d="M3 11l19-9-9 19-2-8-8-2z"/></>,
    building:   <><rect x="3" y="9" width="18" height="12"/><path d="M8 21V9"/><path d="M16 21V9"/><path d="M3 9l9-6 9 6"/></>,
    monitor:    <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
      {paths[name] || null}
    </svg>
  );
};

// ── UploadField ──────────────────────────────────────────────────────────────
const UploadField = ({ label, required, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}{required && <span style={{ color: "var(--red)", marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.4 }}>{hint}</div>}
  </div>
);

export default function CampaignsList() {
  const [campaigns, setCampaigns] = useState([]);
  const [mediaAssets, setMediaAssets] = useState([]);
  const [condominiums, setCondominiums] = useState([]); // condominios com telas
  const [cities, setCities] = useState([]); // cidades com telas
  const [screens, setScreens] = useState([]); // telas para busca
  const [condoSearchTerm, setCondoSearchTerm] = useState(""); // para autocomplete
  const [showCondoDropdown, setShowCondoDropdown] = useState(false); // controlar visibilidade da dropdown
  const [selectedCondo, setSelectedCondo] = useState(null); // condo selecionado com dados completos
  const [citySearchTerm, setCitySearchTerm] = useState(""); // para autocomplete de cidades
  const [showCityDropdown, setShowCityDropdown] = useState(false); // controlar visibilidade da dropdown de cidades
  const [screenSearchTerm, setScreenSearchTerm] = useState(""); // para busca de telas
  const [screenSearchResults, setScreenSearchResults] = useState([]); // resultados da busca de telas
  const [showScreenDropdown, setShowScreenDropdown] = useState(false); // controlar visibilidade da dropdown de telas
  const [selectedScreens, setSelectedScreens] = useState([]); // múltiplas telas selecionadas
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatingStatusIds, setUpdatingStatusIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState(""); // para filtro de pesquisa de campanhas
  const [activeQuickFilter, setActiveQuickFilter] = useState(null); // null | 'expiring' | 'pending'

  // Modal de criar/editar campanha
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [campaignActive, setCampaignActive] = useState(true);
  const [campaignValidFrom, setCampaignValidFrom] = useState("");
  const [campaignValidTo, setCampaignValidTo] = useState("");
  const [campaignTargetType, setCampaignTargetType] = useState("ALL");
  const [campaignTargetRef, setCampaignTargetRef] = useState(""); // para SCREEN, string única
  const [selectedCondos, setSelectedCondos] = useState([]); // para CONDO, array de objetos {id, name, address, etc}
  const [selectedCities, setSelectedCities] = useState([]); // para CITY, array de objetos {name, uf}
  const [saving, setSaving] = useState(false);

  // Modal de itens
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignItems, setCampaignItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Modal de visualização de segmentação
  const [showSegmentationModal, setShowSegmentationModal] = useState(false);
  const [segmentationData, setSegmentationData] = useState(null);

  // Modal de detalhes de sincronização
  const [showSyncPendingModal, setShowSyncPendingModal] = useState(false);
  const [syncPendingData, setSyncPendingData] = useState(null);

  // Confirm delete
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Adicionar item
  const [selectedMediaAsset, setSelectedMediaAsset] = useState("");
  const [itemWeight, setItemWeight] = useState("1");
  const [itemOrder, setItemOrder] = useState("0");
  const [addingItem, setAddingItem] = useState(false);

  // Autocomplete de mídia no modal de itens
  const [mediaSearchTerm, setMediaSearchTerm] = useState("");
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);

  // Preview do modal de itens
  const [previewLayout, setPreviewLayout] = useState('layout2');
  const [previewIdx, setPreviewIdx]       = useState(0);   // índice do item ciclando
  const [previewFade, setPreviewFade]     = useState(true); // flicker de fade
  const previewTimerRef = useRef(null);
  // Espaço livre da coluna do preview — a TV de mentira cresce até preenchê-lo, sem rolar
  const [previewBox, setPreviewBox] = useState({ w: 0, h: 0 });
  const previewObserver = useRef(null);
  const previewAreaRef = React.useCallback((el) => {
    if (previewObserver.current) { previewObserver.current.disconnect(); previewObserver.current = null; }
    if (!el || typeof ResizeObserver === 'undefined') return;
    previewObserver.current = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      setPreviewBox((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    previewObserver.current.observe(el);
  }, []);

  // Cicla pelos itens cadastrados na campanha (igual ao player)
  // Para quando o usuário seleciona uma nova mídia para adicionar.
  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (!showItemsModal || selectedMediaAsset || campaignItems.length === 0) return;

    const sorted = [...campaignItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const advance = () => {
      setPreviewFade(false);
      setTimeout(() => {
        setPreviewIdx(prev => (prev + 1) % sorted.length);
        setPreviewFade(true);
      }, 400);
    };

    const cur = sorted[previewIdx] ?? sorted[0];
    const dur = Math.max(3000, (cur?.mediaAssetId?.durationSec || 8) * 1000);
    previewTimerRef.current = setTimeout(advance, dur);
    return () => clearTimeout(previewTimerRef.current);
  // eslint-disable-next-line
  }, [showItemsModal, selectedMediaAsset, campaignItems, previewIdx]);

  // Reseta índice ao abrir o modal
  useEffect(() => {
    if (showItemsModal) { setPreviewIdx(0); setPreviewFade(true); }
  }, [showItemsModal]);

  // Drag-and-drop de itens
  const dragItemId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    loadCampaigns();
    loadMediaAssets();
    loadCondominiums();
    loadCities();
    loadScreens();
  }, []);

  async function loadCampaigns() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/admin/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao carregar campanhas");
      }

      const data = await r.json();
      setCampaigns(data.campaigns || []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Erro ao carregar campanhas");
    } finally {
      setLoading(false);
    }
  }

  async function loadMediaAssets() {
    try {
      const r = await fetch(`${API_URL}/admin/media-assets?active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        setMediaAssets(data.mediaAssets || []);
      }
    } catch (e) {
      console.error("Erro ao carregar mídias:", e);
    }
  }

  async function loadCondominiums() {
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        const screens = data.screens || [];
        
        // Agrupar por condominio e remover duplicados, mantendo todos os dados
        const condoMap = new Map();
        screens.forEach((screen) => {
          const condoId = screen.superlogicaCondoId;
          if (condoId && !condoMap.has(condoId)) {
            condoMap.set(condoId, {
              id: condoId,
              name: screen.superlogicaCondoName,
              address: screen.superlogicaCondoAddress,
              city: screen.superlogicaCondoCity || screen.city,
              uf: screen.superlogicaCondoUf || screen.uf,
              cep: screen.superlogicaCondoCep,
            });
          }
        });

        // Converter para array e ordenar por nome
        const condoList = Array.from(condoMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));
        
        
        setCondominiums(condoList);
      }
    } catch (e) {
      console.error("Erro ao carregar condominios:", e);
    }
  }

  async function loadCities(condoId) {
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        let screens = data.screens || [];
        
        // Filtrar apenas screens do condomínio especificado
        if (condoId) {
          screens = screens.filter((screen) => screen.superlogicaCondoId === condoId);
        }
        
        // Agrupar por cidade e remover duplicados
        const cityMap = new Map();
        screens.forEach((screen) => {
          const city = screen.superlogicaCondoCity || screen.city;
          const uf = screen.superlogicaCondoUf || screen.uf;
          if (city && uf) {
            const key = `${city}|${uf}`;
            if (!cityMap.has(key)) {
              cityMap.set(key, {
                name: city,
                uf: uf,
              });
            }
          }
        });

        // Converter para array e ordenar por nome
        const cityList = Array.from(cityMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setCities(cityList);
      }
    } catch (e) {
      console.error("Erro ao carregar cidades:", e);
    }
  }

  async function loadScreens() {
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        setScreens(data.screens || []);
      }
    } catch (e) {
      console.error("Erro ao carregar telas:", e);
    }
  }

  function searchScreens(term) {
    if (!term || term.length < 2) {
      setScreenSearchResults([]);
      return;
    }

    const lowerTerm = term.toLowerCase();
    const results = screens.filter((screen) => {
      const screenName = (screen.name || "").toLowerCase();
      const condoName = (screen.superlogicaCondoName || "").toLowerCase();
      const city = (screen.superlogicaCondoCity || screen.city || "").toLowerCase();
      
      return screenName.includes(lowerTerm) || condoName.includes(lowerTerm) || city.includes(lowerTerm);
    });

    setScreenSearchResults(results);
  }

  function handleScreenSearchChange(value) {
    setScreenSearchTerm(value);
    searchScreens(value);
    setShowScreenDropdown(true);
  }

  function getScreenLabel(screen) {
    const screenName = screen.name || "Sem nome";
    const condoName = screen.superlogicaCondoName || "Sem condomínio";
    const city = screen.superlogicaCondoCity || screen.city || "Sem cidade";
    const uf = screen.superlogicaCondoUf || screen.uf || "UF";
    return `${screenName} - ${condoName} - ${city}/${uf}`;
  }

  function renderMediaLayoutBadge(asset) {
    const layouts = asset.compatibleLayouts?.length ? asset.compatibleLayouts : [];
    if (layouts.length === 0) return null;
    const portrait = layouts.includes("layout4") && !layouts.includes("layout2");
    const landscape = !layouts.includes("layout4") && layouts.length > 0;
    if (portrait)
      return <Badge bg="primary" style={{ fontSize: "0.65rem", marginRight: 4 }}>L4</Badge>;
    if (landscape)
      return <Badge bg="success" style={{ fontSize: "0.65rem", marginRight: 4 }}>L2 L3</Badge>;
    return null;
  }

  function openCreateModal() {
    setEditingCampaign(null);
    setCampaignName("");
    setCampaignActive(true);
    setCampaignValidFrom("");
    setCampaignValidTo("");
    setCampaignTargetType("ALL");
    setCampaignTargetRef("");
    setCondoSearchTerm("");
    setSelectedCondo(null);
    setSelectedCondos([]);
    setCitySearchTerm("");
    setSelectedCities([]);
    setScreenSearchTerm("");
    setScreenSearchResults([]);
    setSelectedScreens([]);
    setShowCampaignModal(true);
  }

  function openEditModal(campaign) {
    setEditingCampaign(campaign);
    setCampaignName(campaign.name);
    setCampaignActive(campaign.active);
    setCampaignValidFrom(
      campaign.validFrom ? new Date(campaign.validFrom).toISOString().slice(0, 10) : ""
    );
    setCampaignValidTo(
      campaign.validTo ? new Date(campaign.validTo).toISOString().slice(0, 10) : ""
    );
    setCampaignTargetType(campaign.targetType);
    
    // Se for condomínio, carregar múltiplos condominiums
    if (campaign.targetType === "CONDO" && campaign.targetRef) {
      const selectedIds = Array.isArray(campaign.targetRef) ? campaign.targetRef : [campaign.targetRef];
      const selected = condominiums.filter((c) => selectedIds.includes(c.id));
      setSelectedCondos(selected);
      setCondoSearchTerm("");
      setSelectedCondo(null);
      setCampaignTargetRef("");
      setCitySearchTerm("");
      setSelectedCities([]);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setSelectedScreens([]);
    } else if (campaign.targetType === "CITY" && campaign.targetRef) {
      const selectedCityRefs = Array.isArray(campaign.targetRef) ? campaign.targetRef : [campaign.targetRef];
      const selected = cities.filter((c) => selectedCityRefs.includes(`${c.name}|${c.uf}`));
      setSelectedCities(selected);
      setCitySearchTerm("");
      setCampaignTargetRef("");
      setCondoSearchTerm("");
      setSelectedCondo(null);
      setSelectedCondos([]);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setSelectedScreens([]);
    } else if (campaign.targetType === "SCREEN" && campaign.targetRef) {
      const selectedIds = Array.isArray(campaign.targetRef) ? campaign.targetRef : [campaign.targetRef];
      const selected = screens.filter((s) => selectedIds.includes(s._id));
      setSelectedScreens(selected);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setCampaignTargetRef("");
      setCondoSearchTerm("");
      setSelectedCondo(null);
      setSelectedCondos([]);
      setCitySearchTerm("");
      setSelectedCities([]);
    } else {
      setCampaignTargetRef(campaign.targetRef || "");
      setCondoSearchTerm("");
      setSelectedCondo(null);
      setSelectedCondos([]);
      setCitySearchTerm("");
      setSelectedCities([]);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setSelectedScreens([]);
    }
    
    setShowCampaignModal(true);
  }

  function closeCampaignModal() {
    setShowCampaignModal(false);
    setEditingCampaign(null);
    setSelectedCondo(null);
    setCondoSearchTerm("");
    setSelectedCondos([]);
    setCitySearchTerm("");
    setSelectedCities([]);
    setScreenSearchTerm("");
    setScreenSearchResults([]);
    setSelectedScreens([]);
  }

  async function handleSaveCampaign() {
    if (!campaignName.trim()) {
      alert("Nome da campanha obrigatório");
      return;
    }

    // Para CONDO, enviar array de IDs
    let targetRef = campaignTargetRef || null;
    if (campaignTargetType === "CONDO") {
      if (selectedCondos.length === 0) {
        alert("Selecione pelo menos um condomínio");
        return;
      }
      targetRef = selectedCondos.map(c => c.id);
    } else if (campaignTargetType === "CITY") {
      if (selectedCities.length === 0) {
        alert("Selecione pelo menos uma cidade");
        return;
      }
      targetRef = selectedCities.map(c => `${c.name}|${c.uf}`);
    } else if (campaignTargetType === "SCREEN") {
      if (selectedScreens.length === 0) {
        alert("Selecione pelo menos uma tela");
        return;
      }
      targetRef = selectedScreens.map(s => s._id);
    }

    setSaving(true);
    try {
      const payload = {
        name: campaignName,
        active: campaignActive,
        validFrom: campaignValidFrom || null,
        validTo: campaignValidTo || null,
        targetType: campaignTargetType,
        targetRef: targetRef,
      };

      const url = editingCampaign
        ? `${API_URL}/admin/campaigns/${editingCampaign._id}`
        : `${API_URL}/admin/campaigns`;

      const method = editingCampaign ? "PUT" : "POST";

      const r = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao salvar campanha");
      }

      await loadCampaigns();
      closeCampaignModal();
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao salvar campanha");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCampaign(id) {
    try {
      const r = await fetch(`${API_URL}/admin/campaigns/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao deletar campanha");
      }

      setDeleteTarget(null);
      await loadCampaigns();
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao deletar campanha");
    }
  }

  function isUpdatingStatus(campaignId) {
    return updatingStatusIds.has(campaignId);
  }

  async function handleCampaignStatusChange(campaign, nextActive) {
    if (!campaign || campaign.active === nextActive) return;

    setUpdatingStatusIds((prev) => {
      const next = new Set(prev);
      next.add(campaign._id);
      return next;
    });

    // Atualização otimista na tabela para resposta imediata.
    setCampaigns((prev) =>
      prev.map((item) =>
        item._id === campaign._id ? { ...item, active: nextActive } : item
      )
    );

    try {
      const r = await fetch(`${API_URL}/admin/campaigns/${campaign._id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: campaign.name,
          active: nextActive,
          validFrom: campaign.validFrom || null,
          validTo: campaign.validTo || null,
          targetType: campaign.targetType,
          targetRef: campaign.targetRef || null,
        }),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao atualizar status da campanha");
      }
    } catch (e) {
      console.error(e);
      // Reverte otimista em caso de erro.
      setCampaigns((prev) =>
        prev.map((item) =>
          item._id === campaign._id ? { ...item, active: campaign.active } : item
        )
      );
      alert(e.message || "Erro ao atualizar status da campanha");
    } finally {
      setUpdatingStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(campaign._id);
        return next;
      });
    }
  }

  async function openItemsModal(campaign) {
    setSelectedCampaign(campaign);
    setSelectedMediaAsset("");
    setItemWeight("1");
    setItemOrder("0");
    setMediaSearchTerm("");
    setShowMediaDropdown(false);
    setCampaignItems([]);
    setShowItemsModal(true);
    await loadCampaignItems(campaign._id);
  }

  function closeItemsModal() {
    setShowItemsModal(false);
    setSelectedCampaign(null);
  }

  async function loadCampaignItems(campaignId) {
    setLoadingItems(true);
    try {
      const r = await fetch(`${API_URL}/admin/campaigns/${campaignId}/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        setCampaignItems(data.items || []);
      }
    } catch (e) {
      console.error("Erro ao carregar itens:", e);
    } finally {
      setLoadingItems(false);
    }
  }

  async function handleAddItem() {
    if (!selectedMediaAsset) {
      alert("Selecione uma mídia");
      return;
    }

    setAddingItem(true);
    try {
      const r = await fetch(
        `${API_URL}/admin/campaigns/${selectedCampaign._id}/items`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mediaAssetId: selectedMediaAsset,
            weight: Number(itemWeight) || 1,
            order: Number(itemOrder) || 0,
          }),
        }
      );

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao adicionar item");
      }

      setSelectedMediaAsset("");
      setMediaSearchTerm("");
      setItemWeight("1");
      setItemOrder("0");
      await loadCampaignItems(selectedCampaign._id);
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao adicionar item");
    } finally {
      setAddingItem(false);
    }
  }

  async function handleReorderItems(newSorted) {
    // Atribuir ordens sequenciais
    const updates = [];
    const updatedMap = {};
    newSorted.forEach((item, i) => {
      updatedMap[item._id] = i;
      if ((item.order ?? 0) !== i) updates.push({ id: item._id, order: i });
    });

    // Atualização otimista local
    setCampaignItems((prev) =>
      prev.map((item) => ({ ...item, order: updatedMap[item._id] ?? item.order }))
    );

    // Persistir no backend
    try {
      await Promise.all(
        updates.map(({ id, order }) =>
          fetch(`${API_URL}/admin/campaigns/${selectedCampaign._id}/items/${id}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ order }),
          })
        )
      );
    } catch (e) {
      console.error("Erro ao reordenar:", e);
      await loadCampaignItems(selectedCampaign._id);
    }
  }

  async function handleDeleteItem(itemId) {
    if (!confirm("Deletar este item?")) return;

    try {
      const r = await fetch(
        `${API_URL}/admin/campaigns/${selectedCampaign._id}/items/${itemId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao deletar");
      }

      await loadCampaignItems(selectedCampaign._id);
    } catch (e) {
      console.error(e);
      alert(e.message || "Erro ao deletar item");
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("pt-BR");
    } catch {
      return "—";
    }
  }

  function formatDateTime(dateStr) {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("pt-BR");
    } catch {
      return "—";
    }
  }

  function pad2(value) {
    return String(Math.max(0, Number(value) || 0)).padStart(2, "0");
  }

  function openSyncPendingModal(syncStatus, title, campaignId) {
    if (!syncStatus || syncStatus.state === "NO_TARGET") return;
    setSyncPendingData({
      title,
      campaignId,
      state: syncStatus.state,
      details: Array.isArray(syncStatus.screenDetails)
        ? syncStatus.screenDetails
        : (Array.isArray(syncStatus.pendingDetails) ? syncStatus.pendingDetails : []),
      pendingScreens: syncStatus.pendingScreens || 0,
      pendingOnlineScreens: syncStatus.pendingOnlineScreens || 0,
      pendingOfflineScreens: syncStatus.pendingOfflineScreens || 0,
      syncedScreens: syncStatus.syncedScreens || 0,
      totalScreens: syncStatus.totalScreens || 0,
    });
    setShowSyncPendingModal(true);
  }

  async function handleResyncPending() {
    if (!syncPendingData?.campaignId) return;
    try {
      const r = await fetch(`${API_URL}/admin/campaigns/${syncPendingData.campaignId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error("Erro ao reenviar sincronização");
      setShowSyncPendingModal(false);
      await loadCampaigns();
    } catch (e) {
      alert(e.message || "Erro ao reenviar sincronização");
    }
  }

  function renderSyncStatus(syncStatus, campaignName, campaignId) {
    if (!syncStatus) return <span className="badge badge-gray">—</span>;
    if (syncStatus.state === "NO_TARGET") return <span className="badge badge-gray">Sem telas</span>;
    if (syncStatus.state === "SYNCED") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span className="badge badge-green" style={{ alignSelf: "flex-start", cursor: "pointer" }} onClick={() => openSyncPendingModal(syncStatus, campaignName, campaignId)}>
            <span className="badge-dot"/>Atualizado
          </span>
          <span className="num" style={{ fontSize: 11, color: "var(--fg-3)" }}>{syncStatus.syncedScreens || 0}/{syncStatus.totalScreens || 0}</span>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <span className="badge badge-amber" style={{ alignSelf: "flex-start", cursor: "pointer" }} onClick={() => openSyncPendingModal(syncStatus, campaignName, campaignId)}>
          <span className="badge-dot"/>Pendente
        </span>
        <span className="num" style={{ fontSize: 11, color: "var(--fg-3)" }}>{syncStatus.syncedScreens || 0}/{syncStatus.totalScreens || 0}</span>
      </div>
    );
  }

  function getFilteredCampaigns() {
    let base = campaigns;

    // Filtros rápidos (chips)
    if (activeQuickFilter === 'ativas') {
      base = base.filter(c => c.active);
    } else if (activeQuickFilter === 'inativas') {
      base = base.filter(c => !c.active);
    } else if (activeQuickFilter === 'pendente') {
      base = base.filter((c) => {
        const s = c.syncStatus;
        return s && s.state !== 'SYNCED' && s.state !== 'NO_TARGET';
      });
    }

    if (!searchTerm || !searchTerm.trim()) {
      return base;
    }    

    const term = searchTerm.toLowerCase().trim();
    
    
    // Normalizar string removendo acentos
    const normalizeString = (str) => {
      return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    };
    
    const normalizedTerm = normalizeString(term);
    
    // Verificar se o termo corresponde a algum condomínio e pegar cidades desses condomínios
    const matchingCondoCities = new Set();
    condominiums.forEach((condo) => {
      if (condo.name) {
        const normalizedName = normalizeString(condo.name);
        if (normalizedName.includes(normalizedTerm)) {
          // Pegar a cidade diretamente do condomínio
          const city = condo.city;
          const uf = condo.uf;
          if (city && uf) {
            matchingCondoCities.add(`${city}|${uf}`);
          } else {
          }
        }
      }
    });
    
    
    return base.filter((campaign) => {
      const name = (campaign.name || "").toLowerCase();
      const targetLabel = getTargetLabel(campaign).toLowerCase();
      const targetSearch = getTargetSearchText(campaign).toLowerCase();
      const status = campaign.active ? "ativo" : "inativo";

      // Busca padrão
      if (
        name.includes(term) ||
        targetLabel.includes(term) ||
        targetSearch.includes(term) ||
        status.includes(term)
      ) {
        return true;
      }
      
      // Se a campanha é de CITY e encontramos condomínios que correspondem à busca
      if (campaign.targetType === "CITY" && matchingCondoCities.size > 0) {
        const cityRefs = Array.isArray(campaign.targetRef) 
          ? campaign.targetRef 
          : campaign.targetRef ? [campaign.targetRef] : [];
        
        
        // Verificar se alguma cidade da campanha corresponde às cidades dos condomínios encontrados
        const matches = cityRefs.some((cityRef) => matchingCondoCities.has(cityRef));
        if (matches) {
        }
        return matches;
      }
      
      return false;
    });
  }

  function getTargetSearchText(campaign) {
    const type = campaign?.targetType;
    const ref = campaign?.targetRef;

    if (type === "CONDO") {
      const ids = Array.isArray(ref) ? ref : ref ? [ref] : [];
      return ids
        .map((id) => condominiums.find((c) => c.id === id)?.name || "")
        .filter(Boolean)
        .join(" ");
    }

    if (type === "CITY") {
      const items = Array.isArray(ref) ? ref : ref ? [ref] : [];
      return items.map((v) => String(v).replace("|", " ")).join(" ");
    }

    if (type === "SCREEN") {
      const ids = Array.isArray(ref) ? ref : ref ? [ref] : [];
      return ids
        .map((id) => {
          const screen = screens.find((s) => s._id === id);
          return screen ? getScreenLabel(screen) : "";
        })
        .filter(Boolean)
        .join(" ");
    }

    return "";
  }

  function getTargetLabel(campaign) {
    switch (campaign.targetType) {
      case "ALL":
        return "Todas as telas";
      case "CONDO":
        if (Array.isArray(campaign.targetRef)) {
          // Para múltiplos, mostrar contagem
          return `${campaign.targetRef.length} condomínios`;
        }
        // Para um único, mostrar o nome
        const condoName = condominiums.find(c => c.id === campaign.targetRef)?.name;
        return `Condomínio: ${condoName || campaign.targetRef || "—"}`;
      case "CITY":
        if (Array.isArray(campaign.targetRef)) {
          return `${campaign.targetRef.length} cidades`;
        }
        return `Cidade: ${campaign.targetRef || "—"}`;
      case "SCREEN":
        if (Array.isArray(campaign.targetRef)) {
          return `${campaign.targetRef.length} telas`;
        }
        const screen = screens.find(s => s._id === campaign.targetRef);
        return `Tela: ${screen?.name || campaign.targetRef || "—"}`;
      default:
        return campaign.targetType;
    }
  }

  function openSegmentationModal(campaign) {
    const type = campaign.targetType;
    const refs = Array.isArray(campaign.targetRef) 
      ? campaign.targetRef 
      : [campaign.targetRef];

    let items = [];
    
    if (type === "CONDO") {
      items = refs
        .map(id => condominiums.find(c => c.id === id))
        .filter(Boolean)
        .map(condo => ({
          key: condo.id,
          name: condo.name,
          city: condo.city,
          uf: condo.uf,
        }));
    } else if (type === "CITY") {
      items = refs.map(ref => {
        const [name, uf] = ref.split("|");
        const cityKey = `${name || ""}|${uf || ""}`;
        return {
          key: cityKey,
          name,
          uf,
        };
      });
    } else if (type === "SCREEN") {
      items = refs
        .map(id => screens.find(s => s._id === id))
        .filter(Boolean)
        .map(screen => ({
          key: screen._id,
          name: screen.name,
          condoName: screen.condoName,
          city: screen.city,
          uf: screen.uf,
        }));
    }

    setSegmentationData({ type, items });
    setShowSegmentationModal(true);
  }

  // ── Derived KPI values ──────────────────────────────────────────────────
  const _now = new Date();
  const _in5 = new Date(_now.getTime() + 5 * 24 * 60 * 60 * 1000);
  const ativas       = campaigns.filter(c => c.active).length;
  const vencendo5    = campaigns.filter(c => { if (!c.validTo) return false; const d = new Date(c.validTo); return d >= _now && d <= _in5; }).length;
  const syncPendente = campaigns.filter(c => { const s = c.syncStatus; return s && s.state !== 'SYNCED' && s.state !== 'NO_TARGET'; }).length;

  return (
    <div className="midias-page">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Campanhas Indoor</h1>
          <div className="page-sub">
            <span className="live-dot"/>
            <span><b style={{ color: "var(--fg)" }}>{ativas}</b> ativas · {campaigns.length} no total</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={loadCampaigns}><Icon name="refresh" size={14}/> Atualizar</button>
          <button className="btn btn-primary" onClick={openCreateModal}><Icon name="plus" size={14}/> Nova campanha</button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 14, padding: "12px 16px", background: "var(--red-soft)", border: "0.5px solid var(--red)", borderRadius: 10, color: "var(--red)", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ border: "none", background: "transparent", color: "var(--red)", cursor: "default", display: "flex" }}><Icon name="x" size={14}/></button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid-kpis">
        <div className="card kpi">
          <div className="kpi-label"><span>Campanhas ativas</span><span className="kpi-icon green"><Icon name="check" size={14}/></span></div>
          <div className="kpi-value" style={{ color: ativas ? "var(--green)" : "var(--fg-3)" }}>{loading ? "—" : ativas}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>de {campaigns.length} no total</span></div>
        </div>
        <div className="card kpi">
          <div className="kpi-label"><span>Vencendo em 5 dias</span><span className="kpi-icon amber"><Icon name="calendar" size={14}/></span></div>
          <div className="kpi-value" style={{ color: vencendo5 ? "var(--amber)" : "var(--fg-3)" }}>{loading ? "—" : vencendo5}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{vencendo5 ? "renovar antes do vencimento" : "nenhuma expirando"}</span></div>
        </div>
        <div className="card kpi">
          <div className="kpi-label"><span>Sincronização pendente</span><span className="kpi-icon red"><Icon name="refresh" size={14}/></span></div>
          <div className="kpi-value" style={{ color: syncPendente ? "var(--red)" : "var(--fg-3)" }}>{loading ? "—" : syncPendente}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{syncPendente ? "telas com playlist desatualizada" : "tudo sincronizado"}</span></div>
        </div>
        <div className="card kpi">
          <div className="kpi-label"><span>Total de campanhas</span><span className="kpi-icon purple"><Icon name="list" size={14}/></span></div>
          <div className="kpi-value">{loading ? "—" : campaigns.length}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{campaigns.filter(c => !c.active).length} inativas</span></div>
        </div>
      </div>

      {/* Filter card */}
      <div className="card" style={{ marginTop: 14, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <div className="input-wrap" style={{ flex: 1, minWidth: 280 }}>
            <label className="input-label">Buscar</label>
            <div className="input">
              <Icon name="search" size={14} color="var(--fg-3)"/>
              <input className="search-bare-input" type="text" placeholder="Filtrar campanhas (nome, segmentação…)" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
              {searchTerm && <button onClick={() => setSearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", cursor: "default" }}><Icon name="x" size={14}/></button>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 14, flexWrap: "wrap" }}>
          {[
            { id: null,       label: "Todas",          n: campaigns.length },
            { id: "ativas",   label: "Ativas",         n: ativas },
            { id: "inativas", label: "Inativas",       n: campaigns.filter(c => !c.active).length },
            { id: "pendente", label: "Sinc. pendente", n: syncPendente },
          ].map(c => (
            <button key={c.id ?? "all"} className={`chip ${activeQuickFilter === c.id ? "on" : ""}`} onClick={() => setActiveQuickFilter(activeQuickFilter === c.id ? null : c.id)}>
              {c.label} <span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.7 }}>{c.n}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table card */}
      <div className="card" style={{ marginTop: 14, padding: "16px 0 8px" }}>
        <div className="card-h" style={{ padding: "0 20px 14px" }}>
          <h3>Lista de campanhas <span className="meta" style={{ marginLeft: 8 }}>{getFilteredCampaigns().length} de {campaigns.length}</span></h3>
        </div>
        {loading ? (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>Carregando campanhas…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 20 }}>Nome</th>
                  <th>Segmentação</th>
                  <th>Status</th>
                  <th>Sincronização</th>
                  <th>Criado em</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th style={{ width: 200, paddingRight: 20 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredCampaigns().map(campaign => (
                  <tr key={campaign._id}>
                    <td style={{ paddingLeft: 20 }}>
                      <span style={{ fontWeight: 600 }}>{campaign.name}</span>
                    </td>
                    <td>
                      {campaign.targetType === "ALL" || !Array.isArray(campaign.targetRef) ? (
                        <span style={{ color: "var(--fg-2)", fontSize: 13 }}>{getTargetLabel(campaign)}</span>
                      ) : (
                        <button onClick={() => openSegmentationModal(campaign)} style={{ border: "none", background: "transparent", color: "var(--accent)", textDecoration: "underline", font: "inherit", cursor: "default", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                          {getTargetLabel(campaign)}<Icon name="arrowRight" size={10} stroke={2}/>
                        </button>
                      )}
                    </td>
                    <td>
                      <div className={`badge ${campaign.active ? "badge-green" : "badge-gray"}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, paddingRight: 6, opacity: isUpdatingStatus(campaign._id) ? 0.7 : 1, position: "relative", cursor: isUpdatingStatus(campaign._id) ? "not-allowed" : "pointer" }}>
                        <span className="badge-dot"/>
                        <span style={{ fontWeight: 600 }}>{campaign.active ? "Ativo" : "Desativado"}</span>
                        <Icon name="arrowDown" size={10} color="currentColor"/>
                        <select
                          aria-label="Alterar status da campanha"
                          value={campaign.active ? "active" : "inactive"}
                          onChange={(e) => handleCampaignStatusChange(campaign, e.target.value === "active")}
                          disabled={isUpdatingStatus(campaign._id)}
                          style={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            border: "none",
                            cursor: isUpdatingStatus(campaign._id) ? "not-allowed" : "pointer",
                          }}
                        >
                          <option value="active">Ativo</option>
                          <option value="inactive">Desativado</option>
                        </select>
                      </div>
                    </td>
                    <td>{renderSyncStatus(campaign.syncStatus, campaign.name, campaign._id)}</td>
                    <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{formatDate(campaign.createdAt)}</td>
                    <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{formatDate(campaign.validFrom)}</td>
                    <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{formatDate(campaign.validTo)}</td>
                    <td style={{ paddingRight: 20 }}>
                      <div className="midia-actions" onClick={e => e.stopPropagation()}>
                        <button className="btn" style={{ height: 30, padding: "0 10px", fontSize: 12, color: "var(--accent)", borderColor: "color-mix(in oklch, var(--accent) 30%, var(--hairline))" }} onClick={() => openItemsModal(campaign)}>
                          <Icon name="list" size={12}/> Itens
                        </button>
                        <button className="midia-act purple" title="Editar" onClick={() => openEditModal(campaign)}><Icon name="edit" size={14}/></button>
                        <button className="midia-act red" title="Excluir" onClick={() => setDeleteTarget(campaign)}><Icon name="trash" size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {getFilteredCampaigns().length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>
                      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <Icon name="megaphone" size={24} color="var(--fg-4)"/>
                        <div>Nenhuma campanha encontrada</div>
                        <button className="btn" style={{ marginTop: 8 }} onClick={() => { setSearchTerm(""); setActiveQuickFilter(null); }}>Limpar filtros</button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ height: 24 }}/>

      {/* ── Modal: Criar / Editar Campanha ── */}
      {showCampaignModal && (
        <div onClick={closeCampaignModal} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "min(520px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface-strong)", backdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: editingCampaign ? "var(--purple-soft)" : "var(--accent-soft)", color: editingCampaign ? "var(--purple)" : "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={editingCampaign ? "edit" : "plus"} size={16}/>
                </span>
                <div>
                  <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, letterSpacing: "-0.01em" }}>{editingCampaign ? "Editar campanha" : "Nova campanha"}</h2>
                  <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}>{editingCampaign ? `Alterando "${editingCampaign.name}"` : "Crie uma playlist de mídias para os condomínios"}</div>
                </div>
              </div>
              <button className="icon-btn" onClick={closeCampaignModal}><Icon name="x" size={16}/></button>
            </div>
            {/* Body */}
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16, overflowY: "auto", flex: 1, minHeight: 0 }}>
              <UploadField label="Nome" required>
                <div className="input">
                  <input className="search-bare-input" type="text" placeholder="Ex: Maio Claro Fibra" value={campaignName} onChange={e => setCampaignName(e.target.value)} disabled={saving}/>
                </div>
              </UploadField>

              <UploadField label="Segmentação" required hint={
                campaignTargetType === "CONDO" && selectedCondos.length > 0 ? selectedCondos.map(c => c.name).join(" · ") :
                campaignTargetType === "CITY" && selectedCities.length > 0 ? selectedCities.map(c => `${c.name}-${c.uf}`).join(" · ") :
                campaignTargetType === "SCREEN" && selectedScreens.length > 0 ? selectedScreens.map(s => s.name || "Tela").join(" · ") :
                "Define onde a campanha será exibida."
              }>
                <div className="input">
                  <select className="hw-bare-select" value={campaignTargetType} onChange={e => setCampaignTargetType(e.target.value)} disabled={saving}>
                    <option value="ALL">Todas as telas</option>
                    <option value="CONDO">Condomínio específico</option>
                    <option value="CITY">Cidade específica</option>
                    <option value="SCREEN">Tela específica</option>
                  </select>
                  <Icon name="arrowDown" size={12} color="var(--fg-3)"/>
                </div>
              </UploadField>

              {campaignTargetType !== "ALL" && (
                <>
                  {campaignTargetType === "CONDO" && (() => {
                    const filteredCondos = condoSearchTerm
                      ? condominiums.filter(c => `${c.name} ${c.city || ""} ${c.uf || ""}`.toLowerCase().includes(condoSearchTerm.toLowerCase()))
                      : condominiums;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="building" size={12}/></span>
                            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Selecione os condomínios</span>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: selectedCondos.length ? "var(--accent-soft)" : "var(--surface-sunken)", color: selectedCondos.length ? "var(--accent)" : "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>{selectedCondos.length}/{condominiums.length}</span>
                          </div>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => setSelectedCondos(condominiums.map(c => ({ id: c.id, name: c.name })))} disabled={selectedCondos.length === condominiums.length} style={{ border: "none", background: "transparent", color: selectedCondos.length === condominiums.length ? "var(--fg-4)" : "var(--accent)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: selectedCondos.length === condominiums.length ? "not-allowed" : "default" }}>Selecionar todas</button>
                            {selectedCondos.length > 0 && <button onClick={() => setSelectedCondos([])} style={{ border: "none", background: "transparent", color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: "default" }}>Limpar</button>}
                          </div>
                        </div>
                        <div className="input" style={{ height: 34 }}>
                          <Icon name="search" size={12} color="var(--fg-3)"/>
                          <input className="search-bare-input" type="text" placeholder="Filtrar condomínios…" value={condoSearchTerm} onChange={e => setCondoSearchTerm(e.target.value)} autoComplete="off" disabled={saving}/>
                          {condoSearchTerm && <button onClick={() => setCondoSearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", padding: 0 }}><Icon name="x" size={12}/></button>}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", margin: "0 -4px" }}>
                          {filteredCondos.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhum condomínio encontrado</div>
                          ) : filteredCondos.map(condo => {
                            const on = selectedCondos.some(sc => sc.id === condo.id);
                            return (
                              <button key={condo.id} onClick={() => setSelectedCondos(on ? selectedCondos.filter(sc => sc.id !== condo.id) : [...selectedCondos, { id: condo.id, name: condo.name }])} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "default", transition: "background .1s ease" }} onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--surface-sunken)"; }} onMouseLeave={e => { e.currentTarget.style.background = on ? "var(--accent-soft)" : "transparent"; }}>
                                <span style={{ width: 18, height: 18, borderRadius: 5, border: on ? "none" : "1.5px solid var(--hairline-strong)", background: on ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .12s ease" }}>
                                  {on && <Icon name="check" size={10} color="white"/>}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{condo.name}</div>
                                  {(condo.city || condo.uf) && <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{[condo.city, condo.uf].filter(Boolean).join("-")}</div>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedCondos.length === 0 && (
                          <div style={{ fontSize: 11, color: "var(--amber)", display: "flex", alignItems: "center", gap: 6 }}>
                            <Icon name="alert" size={11}/> Selecione ao menos um condomínio para salvar.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {campaignTargetType === "CITY" && (() => {
                    const filteredCities = citySearchTerm
                      ? cities.filter(c => `${c.name} ${c.uf}`.toLowerCase().includes(citySearchTerm.toLowerCase()))
                      : cities;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="building" size={12}/></span>
                            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Selecione as cidades</span>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: selectedCities.length ? "var(--accent-soft)" : "var(--surface-sunken)", color: selectedCities.length ? "var(--accent)" : "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>{selectedCities.length}/{cities.length}</span>
                          </div>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => setSelectedCities([...cities])} disabled={selectedCities.length === cities.length} style={{ border: "none", background: "transparent", color: selectedCities.length === cities.length ? "var(--fg-4)" : "var(--accent)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: selectedCities.length === cities.length ? "not-allowed" : "default" }}>Selecionar todas</button>
                            {selectedCities.length > 0 && <button onClick={() => setSelectedCities([])} style={{ border: "none", background: "transparent", color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: "default" }}>Limpar</button>}
                          </div>
                        </div>
                        <div className="input" style={{ height: 34 }}>
                          <Icon name="search" size={12} color="var(--fg-3)"/>
                          <input className="search-bare-input" type="text" placeholder="Filtrar cidades…" value={citySearchTerm} onChange={e => setCitySearchTerm(e.target.value)} autoComplete="off" disabled={saving}/>
                          {citySearchTerm && <button onClick={() => setCitySearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", padding: 0 }}><Icon name="x" size={12}/></button>}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", margin: "0 -4px" }}>
                          {filteredCities.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhuma cidade encontrada</div>
                          ) : filteredCities.map(city => {
                            const on = selectedCities.some(sc => sc.name === city.name && sc.uf === city.uf);
                            return (
                              <button key={`${city.name}|${city.uf}`} onClick={() => setSelectedCities(on ? selectedCities.filter(c => !(c.name === city.name && c.uf === city.uf)) : [...selectedCities, city])} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "default", transition: "background .1s ease" }} onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--surface-sunken)"; }} onMouseLeave={e => { e.currentTarget.style.background = on ? "var(--accent-soft)" : "transparent"; }}>
                                <span style={{ width: 18, height: 18, borderRadius: 5, border: on ? "none" : "1.5px solid var(--hairline-strong)", background: on ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .12s ease" }}>
                                  {on && <Icon name="check" size={10} color="white"/>}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{city.name}</div>
                                  <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{city.uf}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedCities.length === 0 && (
                          <div style={{ fontSize: 11, color: "var(--amber)", display: "flex", alignItems: "center", gap: 6 }}>
                            <Icon name="alert" size={11}/> Selecione ao menos uma cidade para salvar.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {campaignTargetType === "SCREEN" && (() => {
                    const filteredScreens = screenSearchTerm
                      ? screens.filter(s => `${s.name || ""} ${s.superlogicaCondoName || ""} ${s.superlogicaCondoCity || s.city || ""}`.toLowerCase().includes(screenSearchTerm.toLowerCase()))
                      : screens;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="monitor" size={12}/></span>
                            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Selecione as telas</span>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: selectedScreens.length ? "var(--accent-soft)" : "var(--surface-sunken)", color: selectedScreens.length ? "var(--accent)" : "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>{selectedScreens.length}/{screens.length}</span>
                          </div>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => setSelectedScreens([...screens])} disabled={selectedScreens.length === screens.length} style={{ border: "none", background: "transparent", color: selectedScreens.length === screens.length ? "var(--fg-4)" : "var(--accent)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: selectedScreens.length === screens.length ? "not-allowed" : "default" }}>Selecionar todas</button>
                            {selectedScreens.length > 0 && <button onClick={() => setSelectedScreens([])} style={{ border: "none", background: "transparent", color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: "default" }}>Limpar</button>}
                          </div>
                        </div>
                        <div className="input" style={{ height: 34 }}>
                          <Icon name="search" size={12} color="var(--fg-3)"/>
                          <input className="search-bare-input" type="text" placeholder="Filtrar telas…" value={screenSearchTerm} onChange={e => setScreenSearchTerm(e.target.value)} autoComplete="off" disabled={saving}/>
                          {screenSearchTerm && <button onClick={() => setScreenSearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", padding: 0 }}><Icon name="x" size={12}/></button>}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", margin: "0 -4px" }}>
                          {filteredScreens.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhuma tela encontrada</div>
                          ) : filteredScreens.map(screen => {
                            const on = selectedScreens.some(ss => ss._id === screen._id);
                            const condoName = screen.superlogicaCondoName || "";
                            const city = screen.superlogicaCondoCity || screen.city || "";
                            const uf = screen.superlogicaCondoUf || screen.uf || "";
                            return (
                              <button key={screen._id} onClick={() => setSelectedScreens(on ? selectedScreens.filter(ss => ss._id !== screen._id) : [...selectedScreens, screen])} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "default", transition: "background .1s ease" }} onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--surface-sunken)"; }} onMouseLeave={e => { e.currentTarget.style.background = on ? "var(--accent-soft)" : "transparent"; }}>
                                <span style={{ width: 18, height: 18, borderRadius: 5, border: on ? "none" : "1.5px solid var(--hairline-strong)", background: on ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .12s ease" }}>
                                  {on && <Icon name="check" size={10} color="white"/>}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{screen.name || "Sem nome"}</div>
                                  {(condoName || city) && <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{[condoName, city && uf ? `${city}-${uf}` : city].filter(Boolean).join(" · ")}</div>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedScreens.length === 0 && (
                          <div style={{ fontSize: 11, color: "var(--amber)", display: "flex", alignItems: "center", gap: 6 }}>
                            <Icon name="alert" size={11}/> Selecione ao menos uma tela para salvar.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <UploadField label="Válido de" required>
                  <div className="input">
                    <input className="search-bare-input" type="date" value={campaignValidFrom} onChange={e => setCampaignValidFrom(e.target.value)} disabled={saving}/>
                    <Icon name="calendar" size={13} color="var(--fg-3)"/>
                  </div>
                </UploadField>
                <UploadField label="Válido até" required>
                  <div className="input">
                    <input className="search-bare-input" type="date" value={campaignValidTo} onChange={e => setCampaignValidTo(e.target.value)} disabled={saving}/>
                    <Icon name="calendar" size={13} color="var(--fg-3)"/>
                  </div>
                </UploadField>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 10, cursor: "default" }}>
                <div onClick={() => !saving && setCampaignActive(!campaignActive)} style={{ width: 36, height: 20, borderRadius: 999, background: campaignActive ? "var(--accent)" : "var(--surface-sunken)", border: "0.5px solid var(--hairline)", position: "relative", transition: "background .15s ease", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 2, left: campaignActive ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left .15s ease" }}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Campanha ativa</div>
                  <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{campaignActive ? "Será exibida nas telas durante o período definido" : "Salva como rascunho — não será sincronizada"}</div>
                </div>
              </label>
            </div>
            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
              <button className="btn" onClick={closeCampaignModal} disabled={saving}>Cancelar</button>
              <button
                className="btn btn-primary"
                disabled={saving || !campaignName.trim() || !campaignValidFrom || !campaignValidTo || (campaignTargetType === "CONDO" && selectedCondos.length === 0) || (campaignTargetType === "CITY" && selectedCities.length === 0) || (campaignTargetType === "SCREEN" && selectedScreens.length === 0)}
                style={{ opacity: (saving || !campaignName.trim() || !campaignValidFrom || !campaignValidTo || (campaignTargetType === "CONDO" && selectedCondos.length === 0) || (campaignTargetType === "CITY" && selectedCities.length === 0) || (campaignTargetType === "SCREEN" && selectedScreens.length === 0)) ? 0.5 : 1 }}
                onClick={handleSaveCampaign}
              >
                {saving ? "Salvando…" : <><Icon name="check" size={14}/> {editingCampaign ? "Salvar alterações" : "Criar campanha"}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Itens da Campanha ── */}
      {showItemsModal && selectedCampaign && (
        <div onClick={closeItemsModal} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "min(1080px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface-strong)", backdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="list" size={16}/></span>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Itens da campanha</div>
                  <h2 style={{ margin: 0, fontSize: 17, letterSpacing: "-0.01em" }}>{selectedCampaign.name}</h2>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 11.5, color: "var(--fg-3)" }}>
                  <b style={{ color: "var(--fg)" }}>{campaignItems.length}</b> {campaignItems.length === 1 ? "item" : "itens"} · peso total <b style={{ color: "var(--fg)" }}>{campaignItems.reduce((a, i) => a + (i.weight || 1), 0)}</b>
                </span>
                <button className="icon-btn" onClick={closeItemsModal}><Icon name="x" size={16}/></button>
              </div>
            </div>
            {/* Body: 2 columns */}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", flex: 1, minHeight: 0, overflow: "hidden" }}>
              {/* Left col: form + list */}
              <div style={{ overflowY: "auto", padding: "18px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
                {/* Add media */}
                <div style={{ background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name="plus" size={12}/>
                    </span>
                    <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Adicionar mídia</h3>
                  </div>

                  {selectedMediaAsset ? (() => {
                    const asset = mediaAssets.find(m => m._id === selectedMediaAsset);
                    if (!asset) return null;
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--accent-soft)", border: "0.5px solid color-mix(in oklch, var(--accent) 30%, transparent)", borderRadius: 10 }}>
                        {asset.publicUrl && asset.type !== "VIDEO" ? <img src={asset.publicUrl} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}/> : asset.type === "VIDEO" ? <div style={{ width: 40, height: 40, borderRadius: 6, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="play" size={14} color="#fff"/></div> : null}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{asset.name || "(sem nome)"}</div>
                          <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{asset.partnerId?.company || "Sem parceiro"} · {asset.durationSec}s</div>
                        </div>
                        <button onClick={() => { setSelectedMediaAsset(""); setMediaSearchTerm(""); }} style={{ border: "none", background: "transparent", color: "var(--fg-3)", display: "flex", cursor: "default", padding: 4 }}><Icon name="x" size={14}/></button>
                      </div>
                    );
                  })() : (
                    <div style={{ position: "relative" }}>
                      <div className="input">
                        <Icon name="search" size={13} color="var(--fg-3)"/>
                        <input className="search-bare-input" type="text" value={mediaSearchTerm} onChange={e => { setMediaSearchTerm(e.target.value); setShowMediaDropdown(true); }} onFocus={() => setShowMediaDropdown(true)} onBlur={() => setTimeout(() => setShowMediaDropdown(false), 200)} disabled={addingItem} placeholder="Digite para buscar mídia…" autoComplete="off"/>
                      </div>
                      {showMediaDropdown && mediaSearchTerm && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, marginTop: 4, background: "var(--surface-strong)", border: "0.5px solid var(--hairline-strong)", borderRadius: 10, boxShadow: "var(--shadow-md)", maxHeight: 260, overflowY: "auto", padding: 4 }}>
                          {mediaAssets.filter(asset => { const t = mediaSearchTerm.toLowerCase(); return (asset.name || "").toLowerCase().includes(t) || (asset.partnerId?.company || "").toLowerCase().includes(t) || (asset.type || "").toLowerCase().includes(t); }).length === 0 ? (
                            <div style={{ padding: 12, fontSize: 12, color: "var(--fg-3)", textAlign: "center" }}>Nenhuma mídia encontrada</div>
                          ) : mediaAssets.filter(asset => { const t = mediaSearchTerm.toLowerCase(); return (asset.name || "").toLowerCase().includes(t) || (asset.partnerId?.company || "").toLowerCase().includes(t) || (asset.type || "").toLowerCase().includes(t); }).map(asset => (
                            <button key={asset._id} onMouseDown={() => { setSelectedMediaAsset(asset._id); setMediaSearchTerm(""); setShowMediaDropdown(false); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "8px 10px", background: "transparent", border: "none", borderRadius: 6, cursor: "default", font: "inherit", color: "inherit", textAlign: "left" }} onMouseEnter={e => e.currentTarget.style.background = "var(--surface-sunken)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              {asset.publicUrl ? (asset.type === "VIDEO" ? <div style={{ width: 36, height: 36, borderRadius: 6, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="play" size={14} color="#fff"/></div> : <img src={asset.publicUrl} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}/>) : <div style={{ width: 36, height: 36, borderRadius: 6, background: "var(--surface-sunken)", flexShrink: 0 }}/>}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{asset.name || asset.partnerId?.company || "(sem nome)"}</div>
                                <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{asset.partnerId?.company || "Sem parceiro"} · {asset.durationSec}s · {asset.type}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Peso (probabilidade)</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10 }}>
                      <div className="input">
                        <input className="search-bare-input" type="number" value={itemWeight} onChange={e => setItemWeight(e.target.value)} disabled={addingItem} min="1" max="20"/>
                        <span style={{ color: "var(--fg-3)", fontSize: 12 }}>×</span>
                      </div>
                      <button className="btn btn-primary" onClick={handleAddItem} disabled={!selectedMediaAsset || addingItem} style={{ opacity: (!selectedMediaAsset || addingItem) ? 0.5 : 1, paddingInline: 16 }}>
                        {addingItem ? "Adicionando…" : <><Icon name="plus" size={14}/> Adicionar</>}
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.4 }}>Quanto maior, mais vezes a mídia aparece na rotação.</div>
                  </div>
                </div>

                {/* Items list */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Itens cadastrados <span style={{ color: "var(--fg-3)", fontWeight: 400 }}>({campaignItems.length})</span></h3>
                    {campaignItems.length > 1 && (
                      <span style={{ fontSize: 11, color: "var(--fg-3)" }}>Arraste para reordenar</span>
                    )}
                  </div>
                  {loadingItems ? (
                    <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>Carregando itens…</div>
                  ) : campaignItems.length === 0 ? (
                    <div style={{ padding: 28, textAlign: "center", border: "1.5px dashed var(--hairline)", borderRadius: 12, color: "var(--fg-3)", fontSize: 12.5 }}>
                      <Icon name="list" size={20} color="var(--fg-4)"/>
                      <div style={{ marginTop: 4 }}>Nenhuma mídia adicionada ainda</div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {[...campaignItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((item, idx) => {
                        const asset = item.mediaAssetId;
                        const isDragOver = dragOverId === item._id;
                        const isBeingDragged = draggingId === item._id;
                        const totalPeso = campaignItems.reduce((acc, i) => acc + (i.weight || 1), 0);
                        const pct = totalPeso > 0 ? Math.round(((item.weight || 1) / totalPeso) * 100) : 0;
                        const sorted = [...campaignItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                        const isActive = sorted.length > 0 && sorted[previewIdx % sorted.length]?._id === item._id;
                        return (
                          <div
                            key={item._id}
                            draggable
                            onDragStart={() => { dragItemId.current = item._id; setDraggingId(item._id); }}
                            onDragEnd={() => { dragItemId.current = null; setDraggingId(null); setDragOverId(null); }}
                            onDragOver={e => { e.preventDefault(); setDragOverId(item._id); }}
                            onDragLeave={() => setDragOverId(null)}
                            onDrop={e => {
                              e.preventDefault(); setDragOverId(null);
                              const fromId = dragItemId.current;
                              if (!fromId || fromId === item._id) return;
                              const s = [...campaignItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                              const fromIdx = s.findIndex(i => i._id === fromId);
                              const toIdx   = s.findIndex(i => i._id === item._id);
                              if (fromIdx === -1 || toIdx === -1) return;
                              const newSorted = [...s];
                              const [moved] = newSorted.splice(fromIdx, 1);
                              newSorted.splice(toIdx, 0, moved);
                              handleReorderItems(newSorted);
                            }}
                            onClick={() => setPreviewIdx(idx)}
                            style={{
                              display: "flex", alignItems: "center", gap: 12,
                              padding: "10px 12px",
                              background: isActive ? "var(--accent-soft)" : "var(--surface)",
                              border: `0.5px solid ${isDragOver ? "var(--accent)" : isActive ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "var(--hairline)"}`,
                              borderRadius: 10, cursor: "default",
                              opacity: isBeingDragged ? 0.4 : 1,
                              boxShadow: isDragOver ? "0 0 0 2px var(--accent-soft)" : "none",
                              transition: "all .12s ease", userSelect: "none",
                            }}
                          >
                            <div style={{ display: "flex", flexDirection: "column", gap: 2, color: "var(--fg-3)", cursor: "grab", padding: "4px 2px", flexShrink: 0 }} title="Arraste para reordenar">
                              <span style={{ width: 12, height: 1.5, background: "currentColor", borderRadius: 1, display: "block" }}/>
                              <span style={{ width: 12, height: 1.5, background: "currentColor", borderRadius: 1, display: "block" }}/>
                              <span style={{ width: 12, height: 1.5, background: "currentColor", borderRadius: 1, display: "block" }}/>
                            </div>
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--fg-3)", fontVariantNumeric: "tabular-nums", minWidth: 18, textAlign: "right", flexShrink: 0 }}>{idx + 1}</span>
                            {asset?.publicUrl && asset?.type !== "VIDEO" ? <img src={asset.publicUrl} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "0.5px solid var(--hairline)", flexShrink: 0 }}/> : asset?.type === "VIDEO" ? <div style={{ width: 40, height: 40, borderRadius: 6, border: "0.5px solid var(--hairline)", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="play" size={16} color="#fff"/></div> : <div style={{ width: 40, height: 40, borderRadius: 6, border: "0.5px solid var(--hairline)", background: "var(--surface-sunken)", flexShrink: 0 }}/>}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <span className={`badge ${asset?.type === "VIDEO" ? "badge-blue" : "badge-green"}`} style={{ fontSize: 9.5, padding: "2px 6px" }}>{asset?.type}</span>
                                <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{asset?.name || "(sem nome)"}</span>
                              </div>
                              <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>
                                {asset?.partnerId?.company && <span style={{ marginRight: 4 }}>{asset.partnerId.company} ·</span>}{asset?.durationSec}s
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                              <span style={{ fontSize: 11, color: "var(--fg-3)" }}>Peso</span>
                              <span style={{ fontWeight: 600, fontSize: 13, fontVariantNumeric: "tabular-nums", minWidth: 18, textAlign: "center", color: isActive ? "var(--accent)" : "var(--fg)" }}>{item.weight || 1}</span>
                              <span style={{ fontSize: 10, color: "var(--fg-3)", fontVariantNumeric: "tabular-nums", minWidth: 28, textAlign: "right" }}>{pct}%</span>
                            </div>
                            <button className="midia-act red" title="Remover item" onClick={e => { e.stopPropagation(); handleDeleteItem(item._id); }} style={{ flexShrink: 0 }}><Icon name="trash" size={14}/></button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Right col: preview */}
              {(() => {
                const sorted       = [...campaignItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                const pickingAsset = selectedMediaAsset ? mediaAssets.find(m => m._id === selectedMediaAsset) : null;
                const cycleItem    = sorted.length > 0 ? sorted[previewIdx % sorted.length] : null;
                const cycleAsset   = cycleItem?.mediaAssetId || null;
                const asset        = pickingAsset || cycleAsset;
                const imgUrl       = asset?.publicUrl || null;
                const isVideo      = asset?.type === 'VIDEO';
                const compatible   = asset?.compatibleLayouts || null;
                const allLayouts   = ['layout2', 'layout4'];
                const availableLayouts = compatible ? allLayouts.filter(lk => compatible.includes(lk)) : allLayouts;
                const safeLayout   = pickingAsset ? (availableLayouts.includes(previewLayout) ? previewLayout : (availableLayouts[0] || 'layout2')) : (availableLayouts[0] || 'layout2');
                const isLayout4    = safeLayout === 'layout4';
                // Réplica do player (tv.html/tv.css/tv.js) no viewport CSS real das TVs (~540×960,
                // ver diagnostics → viewport): os valores em px/vw do tv.css entram literalmente
                const VW = 540, VH = 960;
                const vw = (n) => VW * n / 100;
                const clamp = (min, v, max) => Math.min(max, Math.max(min, v));
                // Maior tela 9:16 que cabe no espaço livre, descontando a moldura
                const BEZEL = 6;
                const fit = Math.min(previewBox.w - BEZEL * 2, (previewBox.h - BEZEL * 2) * VW / VH);
                const innerW = fit > 0 ? Math.floor(fit) : 188;
                const innerH = Math.round(innerW * VH / VW);
                const PS = innerW / VW;
                // Rodapé real de uma tela (o player não tem rodapé padrão)
                const footerUrl = (Array.isArray(screens) ? screens.find((sc) => sc.footerMediaUrl) : null)?.footerMediaUrl || null;
                // Relógio (tv.js: updateClock) — "Ter. 13 de out."
                const now = new Date();
                const clockTime = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false }).format(now);
                const clockDate = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', weekday: 'short', day: 'numeric', month: 'short' }).format(now)
                  .replace(/^(\w+)[,.]?\s+/, (m, w) => `${w.charAt(0).toUpperCase()}${w.slice(1).toLowerCase()}. `)
                  .replace(/(\w{3})$/, '$1.');
                const clockTheme = !isLayout4
                  ? { bg: 'rgba(90,90,90,0.50)', border: '1px solid rgba(255,255,255,0.25)', shadow: '0 2px 10px rgba(0,0,0,0.25)', time: '#ffffff', timeShadow: '0 2px 6px rgba(0,0,0,0.45)', date: '#f0f0f0', dateShadow: '0 1px 3px rgba(0,0,0,0.4)' }
                  : { bg: 'rgba(255,255,255,0.55)', border: '1.5px solid rgba(0,0,0,0.18)', shadow: 'none', time: 'rgba(55,55,62,0.75)', timeShadow: 'none', date: 'rgba(75,75,83,0.65)', dateShadow: 'none' };
                // .media img/video: object-fit contain
                const renderMedia  = () => {
                  if (isVideo && imgUrl) return <video key={imgUrl} src={imgUrl} autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}/>;
                  if (imgUrl) return <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}/>;
                  return <span style={{ color: '#bfbfbf', fontSize: 22, textAlign: 'center', padding: 24 }}>Sem mídia disponível.</span>;
                };
                return (
                  <div style={{ borderLeft: "0.5px solid var(--hairline)", padding: "18px 22px", background: "var(--surface-sunken)", display: "flex", flexDirection: "column", gap: 14, overflow: "hidden", minHeight: 0 }}>
                    <div style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Preview
                      {sorted.length > 0 && !pickingAsset && <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 6, color: "var(--fg-4)" }}>{(previewIdx % sorted.length) + 1}/{sorted.length}</span>}
                      {pickingAsset && <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 6, color: "var(--accent)" }}>nova mídia</span>}
                    </div>
                    <div style={{ display: "flex", flexShrink: 0, padding: 2, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 8, overflow: "hidden" }}>
                      {availableLayouts.map((lk, i) => {
                        const active = safeLayout === lk;
                        const LAYOUT_LABELS = { layout2: "Retrato Padrão", layout3: "Assembleia", layout4: "Retrato Comunicados" };
                        return <button key={lk} onClick={() => pickingAsset && setPreviewLayout(lk)} style={{ flex: 1, padding: "4px 0", fontSize: 11, border: "none", borderLeft: i > 0 ? "0.5px solid var(--hairline)" : "none", background: active ? "var(--accent)" : "transparent", color: active ? "#fff" : "var(--fg-3)", cursor: "default", fontWeight: active ? 700 : 400, opacity: pickingAsset ? 1 : (active ? 1 : 0.4) }}>{LAYOUT_LABELS[lk] || lk}</button>;
                      })}
                    </div>
                    {/* Espaço livre (medido) — a TV cresce até preencher */}
                    <div ref={previewAreaRef} style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: innerW + BEZEL * 2, height: innerH + BEZEL * 2, boxSizing: "border-box", background: "#000", border: `${BEZEL}px solid #222`, borderRadius: 10, overflow: "hidden", position: "relative", boxShadow: "0 4px 12px rgba(0,0,0,0.5)", flexShrink: 0 }}>
                      <div style={{ width: VW, height: VH, transform: `scale(${PS})`, transformOrigin: "top left", position: "absolute", top: 0, left: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#000", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif", WebkitFontSmoothing: "antialiased" }}>
                        {/* Relógio (.clock) */}
                        <div style={{ position: "absolute", top: 20, right: 30, zIndex: 1000, textAlign: "right", lineHeight: 1.2, background: clockTheme.bg, border: clockTheme.border, boxShadow: clockTheme.shadow, padding: "8px 14px", borderRadius: 12, pointerEvents: "none" }}>
                          <span style={{ display: "block", fontSize: clamp(18, vw(3), 32), fontWeight: 700, lineHeight: 1, letterSpacing: "0.5px", color: clockTheme.time, textShadow: clockTheme.timeShadow }}>{clockTime}</span>
                          <span style={{ display: "block", fontSize: clamp(6, vw(1.1), 10), fontWeight: 500, marginTop: 3, letterSpacing: "0.2px", whiteSpace: "nowrap", opacity: 0.8, color: clockTheme.date, textShadow: clockTheme.dateShadow }}>{clockDate}</span>
                        </div>
                        {/* .app: altura = tela − rodapé */}
                        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#000" }}>
                          {isLayout4 ? (
                            <div style={{ flex: 1, minHeight: 0, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", opacity: previewFade ? 1 : 0, transition: "opacity 0.3s ease" }}>{renderMedia()}</div>
                          ) : (
                            <>
                              {/* .media-area: quadrado 100vw */}
                              <div style={{ flex: "0 0 auto", width: VW, height: VW, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", zIndex: 50, opacity: previewFade ? 1 : 0, transition: "opacity 0.3s ease" }}>{renderMedia()}</div>
                              {/* .notices-area + card (layout2: cantos 37px, sobe 30px sobre a mídia) */}
                              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: "#ffffff", position: "relative", zIndex: 100 }}>
                                <div style={{ flex: 1, minHeight: 0, overflow: "hidden", borderRadius: "37px 37px 0 0", marginTop: -30, boxShadow: "0 24px 64px rgba(0,0,0,0.25), 0 12px 32px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.12)", background: "linear-gradient(180deg, #ffffff 0%, #f3f5f8 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <span style={{ color: "#ccc", fontSize: 16, fontStyle: "italic" }}>comunicado</span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        {/* .footer-bar: largura total, altura pela proporção da imagem */}
                        {footerUrl && (
                          <div style={{ flexShrink: 0, lineHeight: 0, fontSize: 0, position: "relative", zIndex: 9999 }}>
                            <img src={footerUrl} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                    <div style={{ flexShrink: 0, fontSize: 11, color: "var(--fg-3)", textAlign: "center", lineHeight: 1.4 }}>
                      {asset ? (
                        <>
                          <strong style={{ color: "var(--fg-2)" }}>{asset.name || asset.partnerId?.company || "(sem nome)"}</strong>
                          <div style={{ fontSize: 10, color: "var(--fg-4)", marginTop: 2 }}>{asset.durationSec}s · {asset.type}{compatible && <> · {compatible.join(", ")}</>}</div>
                        </>
                      ) : <span style={{ color: "var(--fg-4)", fontStyle: "italic" }}>nenhum item na campanha</span>}
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
              <span style={{ fontSize: 11.5, color: "var(--fg-3)" }}>Alterações são salvas ao fechar</span>
              <button className="btn btn-primary" onClick={closeItemsModal}><Icon name="check" size={14}/> Concluir</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Segmentação ── */}
      {showSegmentationModal && segmentationData && (
        <div onClick={() => setShowSegmentationModal(false)} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "min(480px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
                  {segmentationData.type === "CONDO" && "Condomínios selecionados"}
                  {segmentationData.type === "CITY" && "Cidades selecionadas"}
                  {segmentationData.type === "SCREEN" && "Telas selecionadas"}
                </h2>
                <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 2 }}>
                  {segmentationData.items.length} {segmentationData.type === "CONDO" ? "condomínio" : segmentationData.type === "CITY" ? "cidade" : "tela"}{segmentationData.items.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button className="icon-btn" onClick={() => setShowSegmentationModal(false)}><Icon name="x" size={16}/></button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {segmentationData.items.length > 0 ? segmentationData.items.map((item, idx) => (
                <div key={item.key || idx} style={{ padding: "10px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{item.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 2 }}>
                    {segmentationData.type === "CONDO" && (item.city && item.uf ? `${item.city}/${item.uf}` : "—")}
                    {segmentationData.type === "CITY" && item.uf}
                    {segmentationData.type === "SCREEN" && [item.condoName, item.city && item.uf ? `${item.city}/${item.uf}` : item.city].filter(Boolean).join(" · ")}
                  </div>
                </div>
              )) : <div style={{ padding: "40px 22px", textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>Nenhum item para exibir</div>}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
              <button className="btn" onClick={() => setShowSegmentationModal(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Detalhes de Sincronização ── */}
      {showSyncPendingModal && syncPendingData && (() => {
        const details    = Array.isArray(syncPendingData.details) ? syncPendingData.details : [];
        const cntPending = details.filter(d => d.syncState === "PENDING").length;
        const cntOnline  = details.filter(d => d.isOnline).length;
        const cntOffline = details.filter(d => !d.isOnline).length;
        return (
          <div onClick={() => setShowSyncPendingModal(false)} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ width: "min(640px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface-strong)", backdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 34, height: 34, borderRadius: 8, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name="refresh" size={16}/>
                  </span>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Detalhes de Sincronização</div>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}>{syncPendingData.title}</h2>
                  </div>
                </div>
                <button className="icon-btn" onClick={() => setShowSyncPendingModal(false)}><Icon name="x" size={16}/></button>
              </div>
              {/* Stats chips */}
              <div style={{ display: "flex", gap: 8, padding: "14px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 10, background: cntPending > 0 ? "var(--amber-soft)" : "var(--surface-sunken)", border: "0.5px solid var(--hairline)" }}>
                  <span style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1, color: cntPending > 0 ? "var(--amber)" : "var(--fg-3)" }}>{pad2(cntPending)}</span>
                  <span style={{ fontSize: 12, color: cntPending > 0 ? "var(--amber)" : "var(--fg-3)" }}>pendentes</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 10, background: "var(--green-soft)", border: "0.5px solid var(--hairline)" }}>
                  <span style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1, color: "var(--green)" }}>{pad2(cntOnline)}</span>
                  <span style={{ fontSize: 12, color: "var(--green)" }}>online</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 10, background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)" }}>
                  <span style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1, color: "var(--fg-3)" }}>{pad2(cntOffline)}</span>
                  <span style={{ fontSize: 12, color: "var(--fg-3)" }}>offline</span>
                </div>
              </div>
              {/* Screen list */}
              <div style={{ overflowY: "auto", flex: 1, padding: "14px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                {details.length > 0 ? details.map(detail => (
                  <div key={detail.screenId} style={{ padding: "12px 14px", borderRadius: 10, border: "0.5px solid var(--hairline)", background: "var(--surface)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: detail.isOnline ? "var(--green)" : "var(--fg-4)" }}/>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{detail.screenName}</div>
                        <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 1 }}>{[detail.condoName, detail.city && detail.uf ? `${detail.city}/${detail.uf}` : detail.city].filter(Boolean).join(" · ") || "Sem localização"}</div>
                        <div style={{ fontSize: 11, color: "var(--fg-4)", marginTop: 2 }}>Último sync: <strong style={{ color: "var(--fg-2)" }}>{formatDateTime(detail.lastSyncAt)}</strong> &nbsp;·&nbsp; Heartbeat: <strong style={{ color: "var(--fg-2)" }}>{formatDateTime(detail.lastSeenAt)}</strong></div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", flexShrink: 0 }}>
                      <span className={`badge ${detail.syncState === "PENDING" ? "badge-amber" : "badge-green"}`}>{detail.syncState === "PENDING" ? "Pendente" : "Atualizado"}</span>
                      <span className={`badge ${detail.isOnline ? "badge-green" : "badge-gray"}`}><span className="badge-dot"/>{detail.isOnline ? "online" : "offline"}</span>
                    </div>
                  </div>
                )) : <div style={{ padding: "40px 0", textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>Sem detalhes de pendência.</div>}
              </div>
              {/* Footer */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderTop: "0.5px solid var(--hairline)" }}>
                <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
                  <strong style={{ color: "var(--fg-2)", fontVariantNumeric: "tabular-nums" }}>{syncPendingData.syncedScreens || 0}</strong> de <strong style={{ color: "var(--fg-2)", fontVariantNumeric: "tabular-nums" }}>{syncPendingData.totalScreens || 0}</strong> telas sincronizadas
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  {cntPending > 0 && (
                    <button className="btn" onClick={handleResyncPending} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Icon name="refresh" size={13}/> Reenviar pendentes
                    </button>
                  )}
                  <button className="btn" onClick={() => setShowSyncPendingModal(false)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal: Confirmar exclusão ── */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)} style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "min(440px, 100%)", background: "var(--surface)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, padding: 24, boxShadow: "var(--shadow-md)" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 600 }}>Excluir campanha</h3>
            <p style={{ fontSize: 13, color: "var(--fg-2)", marginBottom: 20, lineHeight: 1.5 }}>
              Tem certeza que deseja excluir a campanha <strong>"{deleteTarget.name}"</strong>? As mídias vinculadas continuarão na biblioteca, mas a playlist será removida.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setDeleteTarget(null)}>Cancelar</button>
              <button className="btn" onClick={() => handleDeleteCampaign(deleteTarget._id)} style={{ background: "var(--red-soft)", color: "var(--red)", borderColor: "transparent" }}>
                <Icon name="trash" size={13}/> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
